import apiClient from './client'

export interface LoginRequest {
  userId: string
  password: string
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  userId: string
  userName: string
  userType: string
  clubManagerYn: string
  /** 회원 일련번호 (상품 문의 본인 여부 등) */
  userSeq?: number
}

export interface SignupRequest {
  userId: string
  password: string
  userName: string
  userNick?: string
  userEmail: string
  userTel: string
  centerAddr?: string
  centerAddrDetail?: string
  workplace?: string
  userPosition?: string
  agreeTerms: boolean
  agreePrivacy: boolean
  agreeThirdParty?: boolean
  agreeMarketing?: boolean
  agreeEmail?: boolean
  agreeSms?: boolean
  agreePush?: boolean
  /* ⚠️ 2026-09-12 추가 — 서버(AuthApiController:374-379)는 처음부터 이 셋도 받는데
     타입에 없었다. PC 는 getPayload() 를 스프레드해 초과 속성 검사를 피해 갔고,
     모바일은 손으로 나열하다 아예 빠뜨렸다 — 그래서 RECEIVE_ADS_KAKAO 가
     221명 전원 N, PAYMENT_POLICY_AGREE 가 218 N 이었다. */
  agreeKakao?: boolean
  agreePaymentPolicy?: boolean
  agreeLocation?: boolean
  /** 가입 환경 — PC · MOBILE(모바일 브라우저) · APP(앱). 2026-09-28: 서버가 MOBILE 로 고정 기록하던 것을 바로잡는다 */
  joinPath?: 'PC' | 'MOBILE' | 'APP'
  /** 본인인증 결과 토큰 — 서버는 이 토큰의 보관 결과로 CI·이름·휴대폰·생년월일·성별을 정한다(2026-09-28) */
  certToken?: string
  clubManagerYn?: string
  userCi?: string
  userDi?: string
  birthday?: string
  gender?: string
  snsId?: string
  provider?: string
  /** 친구추천 코드 — 약관 제4편 제2조(추천인 1,000 / 피추천인 500). 선택 입력 */
  referralCode?: string
}

export interface SignupResponse {
  userId: string
  userName: string
  /** 친구추천 적립이 실제로 지급됐는지 — 코드가 없거나 무효면 false */
  referralRewarded?: boolean
}

export interface ApiResponse<T> {
  success?: boolean
  code?: number
  message: string
  data: T
  errorCode?: string
}

/**
 * 로그인 API
 */
export const login = async (credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
  const response = await apiClient.post<ApiResponse<LoginResponse>>('/api/auth/login', credentials)
  
  // React Native 앱에 로그인 성공 알림
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({
      type: 'LOGIN',
      accessToken: response.data.data.accessToken,
      refreshToken: response.data.data.refreshToken,
      userId: response.data.data.userId,
    }))
  }
  
  return response.data
}

/**
 * 로그아웃 API
 */
export const logout = async (): Promise<ApiResponse<void>> => {
  const response = await apiClient.post<ApiResponse<void>>('/api/auth/logout')
  
  // React Native 앱에 로그아웃 알림
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({
      type: 'LOGOUT',
    }))
  }
  
  return response.data
}

/**
 * 토큰 갱신 API
 */
export const refreshToken = async (refreshToken: string): Promise<ApiResponse<{ accessToken: string; refreshToken: string }>> => {
  const response = await apiClient.post<ApiResponse<{ accessToken: string; refreshToken: string }>>('/api/auth/refresh', {
    refreshToken,
  })
  return response.data
}

/**
 * 회원가입 API
 */
export const signup = async (data: SignupRequest): Promise<ApiResponse<SignupResponse>> => {
  const response = await apiClient.post<ApiResponse<SignupResponse>>('/api/auth/signup', data)
  return response.data
}

/**
 * 아이디 중복 확인 API
 */
export const checkUserId = async (userId: string): Promise<ApiResponse<{ available: boolean }>> => {
  const response = await apiClient.get<ApiResponse<{ available: boolean }>>('/api/auth/check-id', {
    params: { userId }
  })
  return response.data
}

/**
 * 이메일 중복 확인 API
 */
export const checkEmail = async (email: string): Promise<ApiResponse<{ available: boolean }>> => {
  const response = await apiClient.get<ApiResponse<{ available: boolean }>>('/api/auth/check-email', {
    params: { email }
  })
  return response.data
}

/** 본인인증 후 CI 중복 경우 — NEW 만 신규 가입 진행 */
export type CiDupCase = 'NEW' | 'ALREADY_REGISTERED' | 'PHONE_CHANGED'

/**
 * 본인인증(CI) 중복 확인 API — 회원가입 본인인증 직후 1폰 1회원 사전 차단 (UI개편 2026-06-04)
 * @param certToken 본인인증 결과 토큰(certResult.certToken). ⛔ 2026-09-28 — CI 를 보내지 않는다.
 *        서버가 보관한 인증 결과의 CI·휴대폰으로 본다(번호 변경 PHONE_CHANGED 판단 포함)
 * @returns available=false 면 이미 가입된 동일인
 */
export const checkCiDuplicate = async (
  certToken: string,
): Promise<ApiResponse<{ available: boolean; joinMethod?: string | null; dupCase?: CiDupCase }>> => {
  const response = await apiClient.post<ApiResponse<{ available: boolean; joinMethod?: string | null; dupCase?: CiDupCase }>>(
    '/api/auth/check-ci',
    { certToken }
  )
  return response.data
}

/**
 * 회원가입 전 휴대폰 중복 확인 (조회만, 저장하지 않음)
 */
export const checkPhone = async (
  phone: string
): Promise<ApiResponse<{ available: boolean; joinMethod?: string | null }>> => {
  const response = await apiClient.post<ApiResponse<{ available: boolean; joinMethod?: string | null }>>(
    '/api/auth/check-phone',
    { phone }
  )
  return response.data
}

/**
 * 번호 변경: 동일 CI 기존 계정의 휴대폰만 본인인증 번호로 갱신
 * ⛔ 2026-09-28 — CI·번호를 보내지 않는다. 서버가 인증 결과(토큰, 1회용)의 CI·번호로 바꾼다.
 */
export const updatePhoneByCi = async (
  certToken: string,
): Promise<ApiResponse<{ joinMethod?: string | null }>> => {
  const response = await apiClient.post<ApiResponse<{ joinMethod?: string | null }>>(
    '/api/auth/update-phone-by-ci',
    { certToken }
  )
  return response.data
}

/** 아이디/비밀번호 찾기 경로 (가입 중복 안내에서 사용) */
export const findAccountPath = (isPc: boolean, phone?: string, name?: string): string => {
  const q = new URLSearchParams()
  if (phone) q.set('phone', phone)
  if (name) q.set('name', name)
  const base = isPc ? '/pc/find-id' : '/mobile/find-id'
  const s = q.toString()
  return s ? `${base}?${s}` : base
}

/**
 * 가입 방식 코드 → 한글 라벨 (중복 안내 confirm 용)
 * SELF=자체(아이디), 나머지는 SNS_TYPE
 */
export const joinMethodLabel = (method?: string | null): string => {
  switch (method) {
    case 'KAKAO': return '카카오'
    case 'NAVER': return '네이버'
    case 'GOOGLE': return '구글'
    case 'APPLE': return '애플'
    case 'SELF': return '아이디(자체 가입)'
    default: return '다른 방식'
  }
}

/**
 * 닉네임 중복 확인 API
 * @param userNick 확인할 닉네임
 * @param excludeUserSeq 본인 USER_SEQ (마이페이지 수정 시 본인 닉 유지 허용)
 */
export const checkNickname = async (
  userNick: string,
  excludeUserSeq?: string | number
): Promise<ApiResponse<{ available: boolean }>> => {
  const params: Record<string, string | number> = { userNick }
  if (excludeUserSeq !== undefined && excludeUserSeq !== null && excludeUserSeq !== '') {
    params.excludeUserSeq = excludeUserSeq
  }
  const response = await apiClient.get<ApiResponse<{ available: boolean }>>('/api/auth/check-nick', {
    params
  })
  return response.data
}

// ========================================
// 아이디 찾기 / 비밀번호 재설정
// ========================================

export interface FindIdRequest {
  name: string
  phone: string
}

export interface FindIdResponse {
  userId: string
  userName: string
  joinDate: string
}

export interface VerifyIdResponse {
  userName: string
}

export interface SendCodeResponse {
  token: string
  expiresIn: number
}

export interface VerifyCodeResponse {
  resetToken: string
}

/**
 * 아이디 찾기 API
 */
export const findId = async (data: FindIdRequest): Promise<ApiResponse<FindIdResponse>> => {
  const response = await apiClient.post<ApiResponse<FindIdResponse>>('/api/auth/find-id', data)
  return response.data
}

/**
 * 비밀번호 재설정을 위한 아이디 확인 API
 */
export const verifyIdForReset = async (userId: string): Promise<ApiResponse<VerifyIdResponse>> => {
  const response = await apiClient.post<ApiResponse<VerifyIdResponse>>('/api/auth/verify-id', { userId })
  return response.data
}

/**
 * 본인인증 코드 발송 API
 */
export const sendVerificationCode = async (userId: string, name: string, phone: string): Promise<ApiResponse<SendCodeResponse>> => {
  const response = await apiClient.post<ApiResponse<SendCodeResponse>>('/api/auth/send-code', { userId, name, phone })
  return response.data
}

/**
 * 인증번호 확인 API
 */
export const verifyCode = async (userId: string, code: string, token: string): Promise<ApiResponse<VerifyCodeResponse>> => {
  const response = await apiClient.post<ApiResponse<VerifyCodeResponse>>('/api/auth/verify-code', { userId, code, token })
  return response.data
}

/**
 * 비밀번호 재설정 API
 */
export const resetPassword = async (userId: string, resetToken: string, newPassword: string): Promise<ApiResponse<void>> => {
  const response = await apiClient.post<ApiResponse<void>>('/api/auth/reset-password', { userId, resetToken, newPassword })
  return response.data
}

// ========================================
// SNS 간편 로그인/가입
// ========================================

export interface SnsSessionResponse {
  snsId: string
  email: string
  name: string
  provider: string
}

export interface SnsSignupRequest {
  snsId: string
  provider: string
  email?: string
  name?: string
  userName: string
  userNick?: string
  userTel: string
  userCi: string
  userDi: string
  birthday: string
  gender: string
  // 약관 동의
  agreeTerms?: boolean
  agreePrivacy?: boolean
  agreeThirdParty?: boolean
  agreeMarketing?: boolean
  agreeEmail?: boolean
  agreeSms?: boolean
  agreePush?: boolean
  /* ⚠️ 2026-09-12 추가 — 서버(AuthApiController:374-379)는 처음부터 이 셋도 받는데
     타입에 없었다. PC 는 getPayload() 를 스프레드해 초과 속성 검사를 피해 갔고,
     모바일은 손으로 나열하다 아예 빠뜨렸다 — 그래서 RECEIVE_ADS_KAKAO 가
     221명 전원 N, PAYMENT_POLICY_AGREE 가 218 N 이었다. */
  agreeKakao?: boolean
  agreePaymentPolicy?: boolean
  agreeLocation?: boolean
  /** 가입 환경 — PC · MOBILE(모바일 브라우저) · APP(앱). 2026-09-28: 서버가 MOBILE 로 고정 기록하던 것을 바로잡는다 */
  joinPath?: 'PC' | 'MOBILE' | 'APP'
  /** 본인인증 결과 토큰 — 서버는 이 토큰의 보관 결과로 CI·이름·휴대폰·생년월일·성별을 정한다(2026-09-28) */
  certToken?: string
  // 추가 정보
  clubManagerYn?: string
  centerAddr?: string
  centerAddrDetail?: string
  workplace?: string
  userPosition?: string
  /** 친구추천 코드 — 약관 제4편 제2조. 선택 입력 */
  referralCode?: string
}

export interface SnsSignupResponse {
  accessToken: string
  refreshToken: string
  userId: string
  userName: string
  userType: string
  clubManagerYn: string
}

/**
 * SNS 세션 데이터 조회 (OAuth 콜백에서 받은 snsToken으로)
 */
export const getSnsSession = async (snsToken: string): Promise<ApiResponse<SnsSessionResponse>> => {
  const response = await apiClient.post<ApiResponse<SnsSessionResponse>>('/api/auth/sns/session', { snsToken })
  return response.data
}

/**
 * SNS 회원가입 (본인인증 결과 포함)
 */
export const snsSignup = async (data: SnsSignupRequest): Promise<ApiResponse<SnsSignupResponse>> => {
  const response = await apiClient.post<ApiResponse<SnsSignupResponse>>('/api/auth/sns/signup', data)
  return response.data
}
